import {BitmapCountUp} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IScreenViewConfig, ScreenView} from "@nolimitcity/slot-game/bin/core/screen/ScreenView";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {EdisonHelper} from "../../../../utils/EdisonHelper";
import {EdisonGameModuleConfig} from "../../../EdisonGameModuleConfig";
import {EdisonSoundConfig} from "../../../EdisonSoundConfig";
import {EdisonGameMode} from "../../../gameMode/EdisonGameMode";
import {EdisonParsedGameData} from "../../../server/data/EdisonParsedGameData";

/**
 * Created by Alican Köker on 2019-03-14.
 */

export class EdisonFreespinOutroView extends ScreenView {

    private _numberOfFSText:PIXI.BitmapText;
    private _outroContainer:PIXI.Container;
    private _outroBackground:PIXI.Sprite;
    private _orientation:Orientation = Orientation.LANDSCAPE;
    private _countUp:BitmapCountUp;

    private _amount:number = 0;
    private _gameData:EdisonParsedGameData;

    private _edisonAutoClose:boolean = false;

    private _numberOfFreespinPlayed:number | undefined = 0;

    constructor(config:IScreenViewConfig, autoClose:boolean = true) {
        super(config, autoClose);
        this.addEventListeners();

        this._edisonAutoClose = autoClose;
    }

    protected onGameDataParsed(data:EdisonParsedGameData):void {

        this._gameData = data;
        this._amount = data.totalWin;
        this._numberOfFreespinPlayed = data.numberOfFreespinsPlayed;

        if(this._numberOfFSText && this._numberOfFreespinPlayed) {
            this._numberOfFSText.text = this._numberOfFreespinPlayed!.toString();
        }

    }

    protected initAnimations():void {
        super.initAnimations();

        this._button.alpha = 0;

        this._numberOfFSText = new PIXI.BitmapText("0", {
            fontName : "NumbersWinCountUp",
            fontSize : 55
        });

        this._numberOfFSText.anchor = .5;

        this._outroBackground = new PIXI.Sprite(GameResources.getTextures("fsOutroBg")[0]);
        this._outroBackground.anchor.set(0.5);
        this._outroBackground.width += 4;
        this._outroBackground.height += 6;

        this._outroContainer = new PIXI.Container();
        this._outroContainer.addChild(this._outroBackground);
        this._outroContainer.addChild(this._numberOfFSText);
        this.addChild(this._outroContainer);

        this._outroContainer.alpha = 0;

        this._countUp = new BitmapCountUp({
            style : {font : {name : "NumbersWinCountUp", size : 80}}
        });

        this._countUp.currentValue = 0;
        this._countUp.alpha = 0;
        this._countUp.visible = false;
        this.addChild(this._countUp);
        this._countUp.getCountUpAnimation(0, 0, {duration : 0});
    }


    protected onResize(resizeData:IResizeData):void {
        super.onResize(resizeData);

        if(this._orientation !== resizeData.orientation)
            this._orientation = resizeData.orientation;

        const screenWidth = resizeData.width / this._layer.scale.x;
        const screenHeight = resizeData.height / this._layer.scale.y;

        if(resizeData.orientation === Orientation.LANDSCAPE) {
            this._numberOfFSText.position.set(screenWidth / 2 - 5, screenHeight / 2 - 140);
            this._countUp.position.set(screenWidth / 2 - 8, screenHeight / 2 + 45);
            this._outroBackground.position.set(screenWidth / 2 - 4, screenHeight / 2 - 30);
        } else {
            this._numberOfFSText.position.set(screenWidth / 2, screenHeight / 2 - 220);
            this._countUp.position.set(screenWidth / 2 - 8, screenHeight / 2 - 40);
            this._outroBackground.position.set(screenWidth / 2, screenHeight / 2 - 108);
        }
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));
    }

    protected startPresentation(data?:ParsedServerData, onShowComplete?:() => void):void {

        let tl:TimelineLite = new TimelineLite();

        const winSize:number = EdisonHelper.getWinRatio(this._gameData!.currentWinRatio);

        SlotGame.sound.fadeAmbience(0, 500);
        SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_OUTRO);

        tl.add(() => TweenLite.to(this._outroContainer, 1, {alpha : 1}));

        tl.add(() => {
            this._countUp.visible = true;
            TweenLite.to(this._countUp, 3, {alpha : 1});
        }, 2);

        tl.add(() => {
            this._countUp.getCountUpAnimation(0, this._amount, {duration : 1.5 + winSize});
            if(this._amount > 0) {
                SlotGame.sound.playEffect(EdisonSoundConfig.instance.COUNT_UP, -1);
            }
        }, 3);

        tl.add(() => {
            if(this._amount > 0) {
                SlotGame.sound.playEffect(EdisonSoundConfig.instance.COUNT_UP_ENDS);
                SlotGame.sound.stopEffect(EdisonSoundConfig.instance.COUNT_UP);
                SlotGame.sound.fadeAmbience(1, 500);
            }
        }, 4.5 + winSize);

        tl.add(() => {
            TweenLite.to(this._button, .5, {alpha : 1, ease : Linear.easeNone});
            this.enableButton();
        }, 7.5 + winSize);
    }

    public show(data?:ParsedServerData, onShowComplete?:() => void):void {
        this._countUp.visible = false;
        this._countUp.alpha = 0;
        this._countUp.getCountUpAnimation(0, 0, {duration : 0});
        super.show(data, onShowComplete);
    }


    protected stopPresentation(closeCallback?:() => void) {
        super.stopPresentation(closeCallback);

        EdisonGameModuleConfig.background.currentMode = EdisonGameMode.NORMAL;

        this._outroContainer.alpha = 0;

        SlotGame.sound.fadeAmbience(0, 500);
        let tl:TimelineLite = new TimelineLite();

        tl.add(() => {
            SlotGame.sound.playAmbience(EdisonSoundConfig.instance.MAIN_GAME_AMBIANCE);
            SlotGame.sound.fadeAmbience(1, 1000);
        }, .5);

        this._button.alpha = 0;
    }
}